// ============================================================
// Deal list sorting + filtering — shared by web (frontend/) and mobile (mobile/).
// Ported verbatim from frontend/src/pages/Deals.jsx so both clients stay aligned.
// ============================================================

import type { DealListItem } from "./models";

export type DealSortKey =
  | "default"
  | "lead"
  | "name"
  | "amount_desc"
  | "amount_asc"
  | "irr_desc"
  | "irr_asc";

// Deals-list sort options. 'default' is the business-priority sort:
// Trouble → Prospect → LOI (by deal status, regardless of capital type),
// then Active → R/E AFS → Passive → R/E LT (by capital composition), then
// everything else, then Exit'd (realised — nothing left to work) last.
export const DEAL_SORT_OPTIONS: { value: DealSortKey; label: string }[] = [
  { value: "default", label: "Default (Status & Type)" },
  { value: "lead", label: "Lead" },
  { value: "name", label: "Name" },
  { value: "amount_desc", label: "Amount (High → Low)" },
  { value: "amount_asc", label: "Amount (Low → High)" },
  { value: "irr_desc", label: "IRR (High → Low)" },
  { value: "irr_asc", label: "IRR (Low → High)" },
];

const nameCmp = (a: DealListItem, b: DealListItem) => (a.name || "").localeCompare(b.name || "");
const amountVal = (d: DealListItem) =>
  d.totalCost != null ? d.totalCost : d.committedCapital != null ? d.committedCapital : 0;
const irrVal = (d: DealListItem) =>
  d.dealActualIRR != null ? d.dealActualIRR : d.estimatedIRR != null ? d.estimatedIRR : null;

// Priority bucket for the default sort (lower = shown first).
const defaultBucket = (d: DealListItem) => {
  // An exited deal is realised and needs no attention, so it sorts last —
  // checked before the capital-composition buckets, which it would otherwise
  // land in (an Exit'd deal can still carry active/passive details).
  if (d.status === "Exit'd") return 8;
  if (d.status === "Trouble") return 0;
  if (d.status === "Prospect") return 1;
  if (d.status === "Under Contract/LOI") return 2;
  if ((d.activeCount || 0) > 0) return 3;
  if ((d.reAfsCount || 0) > 0) return 4;
  if ((d.passiveCount || 0) > 0) return 5;
  if ((d.reLtCount || 0) > 0) return 6;
  return 7;
};

// Compare nullable IRR values: nulls always sort to the end regardless of direction.
const irrCmp = (a: DealListItem, b: DealListItem, dir: "asc" | "desc") => {
  const x = irrVal(a),
    y = irrVal(b);
  if (x == null && y == null) return nameCmp(a, b);
  if (x == null) return 1;
  if (y == null) return -1;
  return (dir === "asc" ? x - y : y - x) || nameCmp(a, b);
};

export function sortDeals<T extends DealListItem>(deals: T[], sortBy: DealSortKey): T[] {
  const sorted = [...deals];
  switch (sortBy) {
    case "name":
      sorted.sort(nameCmp);
      break;
    case "amount_desc":
      sorted.sort((a, b) => amountVal(b) - amountVal(a) || nameCmp(a, b));
      break;
    case "amount_asc":
      sorted.sort((a, b) => amountVal(a) - amountVal(b) || nameCmp(a, b));
      break;
    case "irr_desc":
      sorted.sort((a, b) => irrCmp(a, b, "desc"));
      break;
    case "irr_asc":
      sorted.sort((a, b) => irrCmp(a, b, "asc"));
      break;
    case "lead":
      sorted.sort((a, b) => {
        const la = (a.harvusLeadNames || "").trim(),
          lb = (b.harvusLeadNames || "").trim();
        if (!la && !lb) return nameCmp(a, b);
        if (!la) return 1;
        if (!lb) return -1;
        return la.localeCompare(lb) || nameCmp(a, b);
      });
      break;
    default:
      sorted.sort((a, b) => defaultBucket(a) - defaultBucket(b) || nameCmp(a, b));
  }
  return sorted;
}

// Hide Dead deals unless showDead, then keyword-match across the same fields
// the web search box covers.
export function filterDeals<T extends DealListItem>(
  deals: T[],
  opts: { search?: string; showDead?: boolean },
): T[] {
  const q = (opts.search || "").trim().toLowerCase();
  return deals
    .filter((d) => opts.showDead || d.status !== "Dead")
    .filter(
      (d) =>
        !q ||
        [d.name, d.harvusLeadNames, d.status, d.dealStructure, d.dealSourceName].some((f) =>
          (f || "").toLowerCase().includes(q),
        ),
    );
}
